import React, { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { SyncOutlined } from '@ant-design/icons';

/**
 * Pull-to-refresh para o PWA (somente touch).
 *
 * Envolve o conteúdo de uma tela e, quando o ancestral rolável está no topo,
 * permite "puxar para baixo" para disparar um refresh. Mostra um indicador
 * (ícone de refresh que gira com o progresso; spinner durante o refresh).
 *
 * Comportamento:
 *  - Só ativa com o scroll no topo (scrollTop === 0); rolando para baixo, o
 *    touchmove não é interceptado e o scroll normal funciona.
 *  - Listener `touchmove` não-passivo (addEventListener) para o preventDefault
 *    funcionar e impedir o rubber-band/refresh nativo durante o puxo.
 *  - Em desktop/sem touch é inerte (sem efeito colateral).
 *
 * Props:
 *  - onRefresh: função async (opcional). Se omitida, recarrega a página
 *    (window.location.reload), mantendo o login via token no localStorage.
 *  - threshold: distância em px para disparar (default 70).
 *  - color: cor do indicador (default azul Bluquimi).
 *  - children: conteúdo envolvido.
 */
const PullToRefresh = ({
  onRefresh,
  threshold = 70,
  color = '#1b3663',
  children,
}) => {
  const wrapRef = useRef(null);
  const startYRef = useRef(null);
  const pullingRef = useRef(false);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);

  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const setPullBoth = (v) => { pullRef.current = v; setPull(v); };
  const setRefreshingBoth = (v) => { refreshingRef.current = v; setRefreshing(v); };

  // Encontra o ancestral rolável mais próximo (layout do PWA) e devolve seu
  // scrollTop. Se não houver, usa o window. Usado para só ativar no topo.
  const findScrollTop = () => {
    let el = wrapRef.current?.parentElement;
    while (el) {
      const ov = getComputedStyle(el).overflowY;
      if (ov === 'auto' || ov === 'scroll') return el.scrollTop;
      el = el.parentElement;
    }
    return typeof window !== 'undefined' ? window.scrollY || 0 : 0;
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    const onStart = (e) => {
      if (refreshingRef.current) return;
      if (findScrollTop() > 0) { pullingRef.current = false; return; }
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = true;
    };

    const onMove = (e) => {
      if (!pullingRef.current || refreshingRef.current) return;
      const dy = e.touches[0].clientY - (startYRef.current ?? 0);
      if (dy <= 0) {
        if (pullRef.current !== 0) setPullBoth(0);
        return;
      }
      // Puxando para baixo no topo: assume o gesto e impede o comportamento nativo.
      e.preventDefault();
      setPullBoth(dy * 0.5); // resistência
    };

    const onEnd = async () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      if (pullRef.current >= threshold) {
        setRefreshingBoth(true);
        setPullBoth(threshold);
        try {
          if (onRefresh) {
            await onRefresh();
          } else if (typeof window !== 'undefined') {
            window.location.reload();
            return; // reload descarrega o app; não há estado para recolher
          }
        } catch (_) { /* ignora falhas do refresh */ }
        setRefreshingBoth(false);
      }
      setPullBoth(0);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [onRefresh, threshold]);

  const progress = Math.min(1, pull / threshold);
  const rotate = progress * 180;

  return (
    <div ref={wrapRef}>
      <div
        style={{
          height: pull,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        aria-hidden="true"
      >
        {refreshing ? (
          <Spin size="small" />
        ) : (
          <SyncOutlined
            style={{ fontSize: 20, color, transform: `rotate(${rotate}deg)`, opacity: progress }}
          />
        )}
      </div>
      <div>{children}</div>
    </div>
  );
};

export default PullToRefresh;
